import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, NetworkError } from '../lib/api';
import type {
  FeatureKey,
  TenantDetail as TenantDetailData,
  TenantDetailResponse,
  TenantFeaturesResponse,
  TenantImpersonateResponse,
} from '../types';
import { formatDate } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';
import { StateBlock } from '../components/StateBlock';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; tenant: TenantDetailData };

/** Carga de la sección de features (independiente del detalle del tenant). */
type FeaturesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; features: Record<string, boolean> };

/**
 * Estado del toggle de UNA feature, trackeado por FeatureKey en un mapa
 * (mismo patrón que `RowState` en Tenants.tsx). Un fallo en un toggle no
 * afecta a los demás: la vista muestra el último estado conocido del
 * servidor — sin estado optimista, un PATCH fallido deja el switch donde
 * el servidor dice que está.
 *  - 'idle'  : sin acción en curso.
 *  - 'busy'  : request PATCH en vuelo (el toggle queda deshabilitado).
 *  - 'error' : el último PATCH falló; message visible junto al toggle.
 */
type ToggleState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'error'; message: string };

const TOGGLE_IDLE: ToggleState = { kind: 'idle' };

/**
 * Estado del botón de impersonation ("Entrar como este comercio").
 *  - 'idle'  : sin acción en curso.
 *  - 'busy'  : request en vuelo; el botón muestra "Abriendo…".
 *  - 'error' : el último intento falló; message visible junto al botón.
 */
type ImpersonateState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'error'; message: string };

/** Orden estable de las 5 features (coincide con `ALL_FEATURES` del backend). */
const FEATURE_ORDER: FeatureKey[] = [
  'shopify',
  'conversions',
  'webhooks',
  'multi_currency',
  'bot_api',
];

/**
 * Labels en español neutro colombiano. El backend NO devuelve labels, así
 * que el mapa vive en la SPA. Mantener alineado con `FEATURE_LABELS` del
 * backend (apps/api/src/lib/features.ts).
 */
const FEATURE_LABELS: Record<FeatureKey, string> = {
  shopify: 'Conector Shopify',
  conversions: 'Atribución de conversiones',
  webhooks: 'Webhooks salientes',
  multi_currency: 'Multi-moneda (listas de precios)',
  bot_api: 'API pública para bots',
};

/**
 * Detalle de un tenant del SaaS: datos, estado de suspensión y toggles de
 * funcionalidades. Requiere la cookie de sesión: un 401 redirige al login.
 */
export function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [features, setFeatures] = useState<FeaturesState>({ kind: 'loading' });
  // Estado por toggle. Clave = FeatureKey. Ausencia = idle.
  const [toggles, setToggles] = useState<Record<string, ToggleState>>({});
  const [impersonate, setImpersonate] = useState<ImpersonateState>({
    kind: 'idle',
  });

  /** Traduce un error a mensaje legible; un 401 redirige al login. */
  const errorMessage = useCallback(
    (err: unknown): string | null => {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login', { replace: true });
        return null;
      }
      if (err instanceof NetworkError) {
        return 'No se pudo conectar con el servidor.';
      }
      if (err instanceof ApiError) return err.message;
      return 'Ocurrió un error inesperado.';
    },
    [navigate],
  );

  const loadTenant = useCallback(async () => {
    if (!id) {
      setState({ kind: 'error', message: 'Falta el identificador del tenant.' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      const res = await api.get<TenantDetailResponse>(
        `/api/super-admin/tenants/${id}`,
      );
      setState({ kind: 'ready', tenant: res.tenant });
    } catch (err) {
      const message = errorMessage(err);
      if (message) setState({ kind: 'error', message });
    }
  }, [id, errorMessage]);

  const loadFeatures = useCallback(async () => {
    if (!id) return;
    setFeatures({ kind: 'loading' });
    try {
      const res = await api.get<TenantFeaturesResponse>(
        `/api/super-admin/tenants/${id}/features`,
      );
      setFeatures({ kind: 'ready', features: res.features });
      setToggles({});
    } catch (err) {
      const message = errorMessage(err);
      if (message) setFeatures({ kind: 'error', message });
    }
  }, [id, errorMessage]);

  useEffect(() => {
    void loadTenant();
    void loadFeatures();
  }, [loadTenant, loadFeatures]);

  const setToggle = useCallback((feature: FeatureKey, next: ToggleState) => {
    setToggles((prev) => ({ ...prev, [feature]: next }));
  }, []);

  const toggleFeature = useCallback(
    async (feature: FeatureKey, enabled: boolean) => {
      if (!id) return;
      setToggle(feature, { kind: 'busy' });
      try {
        const res = await api.patch<TenantFeaturesResponse>(
          `/api/super-admin/tenants/${id}/features`,
          { feature, enabled },
        );
        // El backend devuelve el estado completo; lo aplicamos tal cual.
        setFeatures({ kind: 'ready', features: res.features });
        setToggle(feature, TOGGLE_IDLE);
      } catch (err) {
        const message = errorMessage(err);
        if (message) setToggle(feature, { kind: 'error', message });
      }
    },
    [id, setToggle, errorMessage],
  );

  /**
   * Inicia una sesión de soporte: el backend setea la cookie de soporte en
   * la respuesta y devuelve la URL del panel del comercio, que se abre en
   * una pestaña nueva. Funciona también con tenants suspendidos.
   */
  const startImpersonation = useCallback(async () => {
    if (!id) return;
    setImpersonate({ kind: 'busy' });
    try {
      const res = await api.post<TenantImpersonateResponse>(
        `/api/super-admin/tenants/${id}/impersonate`,
      );
      // 'noopener' corta la referencia window.opener — la pestaña del panel
      // del comercio no puede manipular este panel de super-admin.
      window.open(res.panelUrl, '_blank', 'noopener');
      setImpersonate({ kind: 'idle' });
    } catch (err) {
      const message = errorMessage(err);
      if (message) setImpersonate({ kind: 'error', message });
    }
  }, [id, errorMessage]);

  return (
    <>
      <header className="app-header">
        <div className="brand">
          <span className="brand-title">Inventario</span>
          <span className="brand-tag">Super Admin</span>
        </div>
      </header>

      <main className="app-main">
        <div className="mb-16">
          <Link to="/tenants" className="back-link">
            ← Tenants
          </Link>
        </div>

        {state.kind === 'loading' && (
          <div className="state-block">
            <div className="state-title">Cargando tenant…</div>
          </div>
        )}

        {state.kind === 'error' && (
          <StateBlock
            title="No se pudo cargar el tenant"
            description={state.message}
            action={{ label: 'Reintentar', onClick: () => void loadTenant() }}
          />
        )}

        {state.kind === 'ready' && (
          <>
            <div className="row-between">
              <div>
                <h1 className="page-title">{state.tenant.name}</h1>
                <p className="page-subtitle">Detalle del comercio.</p>
              </div>
              {state.tenant.suspended ? (
                <StatusBadge status="Suspendido" />
              ) : (
                <StatusBadge
                  status={state.tenant.connected ? 'conectado' : 'pendiente'}
                />
              )}
            </div>

            <section className="detail-card mt-24">
              <h2 className="section-title">Datos del tenant</h2>
              <dl className="detail-grid">
                <div className="detail-item">
                  <dt>Identificador</dt>
                  <dd className="mono">{state.tenant.id}</dd>
                </div>
                <div className="detail-item">
                  <dt>Plan</dt>
                  <dd style={{ textTransform: 'capitalize' }}>
                    {state.tenant.plan}
                  </dd>
                </div>
                <div className="detail-item">
                  <dt>Cuenta Whatsfy</dt>
                  <dd className="mono">{state.tenant.whatsfyAccountId}</dd>
                </div>
                <div className="detail-item">
                  <dt>Creado</dt>
                  <dd>{formatDate(state.tenant.createdAt)}</dd>
                </div>
                <div className="detail-item">
                  <dt>Locale</dt>
                  <dd>{state.tenant.locale ?? '—'}</dd>
                </div>
                <div className="detail-item">
                  <dt>Zona horaria</dt>
                  <dd>{state.tenant.timezone ?? '—'}</dd>
                </div>
              </dl>
            </section>

            <section className="detail-card mt-16">
              <h2 className="section-title">Conteos</h2>
              <dl className="detail-grid">
                <div className="detail-item">
                  <dt>Usuarios</dt>
                  <dd className="cell-num">{state.tenant.counts.users}</dd>
                </div>
                <div className="detail-item">
                  <dt>Productos</dt>
                  <dd className="cell-num">{state.tenant.counts.products}</dd>
                </div>
                <div className="detail-item">
                  <dt>Canales</dt>
                  <dd className="cell-num">{state.tenant.counts.channels}</dd>
                </div>
                <div className="detail-item">
                  <dt>API keys públicas</dt>
                  <dd className="cell-num">
                    {state.tenant.counts.publicApiKeys}
                  </dd>
                </div>
                <div className="detail-item">
                  <dt>Pagos pendientes</dt>
                  <dd className="cell-num">
                    {state.tenant.counts.pendingPayments}
                  </dd>
                </div>
              </dl>
            </section>

            {state.tenant.suspended && (
              <section className="detail-card detail-card-warn mt-16">
                <h2 className="section-title">Suspensión</h2>
                <dl className="detail-grid">
                  <div className="detail-item">
                    <dt>Motivo</dt>
                    <dd>{state.tenant.suspendedReason ?? '—'}</dd>
                  </div>
                  <div className="detail-item">
                    <dt>Suspendido por</dt>
                    <dd>{state.tenant.suspendedBy ?? '—'}</dd>
                  </div>
                  <div className="detail-item">
                    <dt>Fecha</dt>
                    <dd>
                      {state.tenant.suspendedAt
                        ? formatDate(state.tenant.suspendedAt)
                        : '—'}
                    </dd>
                  </div>
                </dl>
              </section>
            )}

            <section className="detail-card mt-16">
              <h2 className="section-title">Funcionalidades</h2>
              <p className="section-note">
                Una funcionalidad desactivada deja de estar disponible para
                todo el equipo del comercio.
              </p>

              {features.kind === 'loading' && (
                <div className="text-muted mt-16" style={{ fontSize: 13 }}>
                  Cargando funcionalidades…
                </div>
              )}

              {features.kind === 'error' && (
                <div className="msg msg-error mt-16" role="alert">
                  {features.message}
                  <div className="mt-8">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => void loadFeatures()}
                    >
                      Reintentar
                    </button>
                  </div>
                </div>
              )}

              {features.kind === 'ready' && (
                <ul className="feature-list mt-16">
                  {FEATURE_ORDER.map((key) => {
                    const enabled = features.features[key] ?? false;
                    const toggle = toggles[key] ?? TOGGLE_IDLE;
                    const busy = toggle.kind === 'busy';
                    return (
                      <li key={key} className="feature-item">
                        <label className="feature-row">
                          <span className="feature-label">
                            {FEATURE_LABELS[key]}
                          </span>
                          <span className="feature-control">
                            {busy && (
                              <span className="feature-status">Guardando…</span>
                            )}
                            <input
                              type="checkbox"
                              className="feature-toggle"
                              checked={enabled}
                              disabled={busy}
                              onChange={(e) =>
                                void toggleFeature(key, e.target.checked)
                              }
                            />
                          </span>
                        </label>
                        {toggle.kind === 'error' && (
                          <div className="cell-error" role="alert">
                            {toggle.message}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="detail-card mt-16">
              <h2 className="section-title">Soporte</h2>
              <p className="section-note">
                Entra al panel del comercio para diagnosticar problemas tal
                como los ve el cliente.
              </p>

              <div className="support-action mt-16">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={impersonate.kind === 'busy'}
                  onClick={() => void startImpersonation()}
                >
                  {impersonate.kind === 'busy'
                    ? 'Abriendo…'
                    : 'Entrar como este comercio'}
                </button>
                <ul className="support-notes">
                  <li>
                    Abre el panel del comercio en modo soporte: ves todo tal
                    como lo ve el cliente, pero en <strong>solo lectura</strong>{' '}
                    (no puedes modificar nada).
                  </li>
                  <li>
                    La sesión de soporte dura 15 minutos. Al expirar, vuelves a
                    este panel para renovarla.
                  </li>
                </ul>
              </div>

              {impersonate.kind === 'error' && (
                <div className="cell-error" role="alert">
                  {impersonate.message}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </>
  );
}
